import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  login as apiLogin,
  register as apiRegister,
  completeOnboarding as apiCompleteOnboarding,
  getUnreadCount,
} from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const response = await getUnreadCount();
      setUnreadCount(response.data.count ?? 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [user]);

  // Clear all auth data
  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setUnreadCount(0);
  }, []);

  // Initialize auth state
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');

    if (storedUser && storedUser !== 'undefined' && storedToken && storedToken !== 'undefined') {
      try {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
      } catch (e) {
        // Corrupted localStorage — clear and start fresh
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Poll unread message count
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [user, fetchUnreadCount]);

  const login = async (credentials) => {
    try {
      const response = await apiLogin(credentials);
      const payload = response.data;
      const { token: newToken, refreshToken: newRefresh, user: userData } = payload;

      localStorage.setItem('token', newToken);
      if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
      localStorage.setItem('user', JSON.stringify(userData));

      setToken(newToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await apiRegister(userData);
      const payload = response.data;
      const { token: newToken, refreshToken: newRefresh, user: newUser } = payload;

      localStorage.setItem('token', newToken);
      if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
      localStorage.setItem('user', JSON.stringify(newUser));

      setToken(newToken);
      setUser(newUser);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
      };
    }
  };

  const completeOnboarding = useCallback(async () => {
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }

    try {
      const response = await apiCompleteOnboarding();
      const updatedUser = {
        ...user,
        onboarding_complete: response.data?.onboarding_complete ?? true,
      };

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Could not complete onboarding',
      };
    }
  }, [user]);

  const logout = () => {
    clearAuth();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        register,
        completeOnboarding,
        logout,
        loading,
        unreadCount,
        fetchUnreadCount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
